import { Contest } from '../../models/types';
import i18n from '../../i18n/i18n-setup';
import { intlLocale } from '../../i18n/locale-format';

// Pace arrives as decimal minutes per km; show it as m:ss.
export const formatPace = (pace: number | undefined | null): string => {
  if (!pace || !isFinite(pace) || pace <= 0) return '–';
  let minutes = Math.floor(pace);
  let seconds = Math.round((pace - minutes) * 60);
  if (seconds === 60) {
    minutes += 1;
    seconds = 0;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const formatKm = (km: number | undefined | null, digits = 1): string => `${(km || 0).toFixed(digits)} km`;

const DAY_MS = 86_400_000;

export interface ContestStatus {
  tone: 'success' | 'accent' | 'muted';
  label: string;
}

export const getContestStatus = (contest: Pick<Contest, 'startAt' | 'endAt'>, now = new Date()): ContestStatus => {
  const start = new Date(contest.startAt).getTime();
  const end = new Date(contest.endAt).getTime();
  const t = now.getTime();
  if (t >= end) return { tone: 'muted', label: i18n.t('contests:status.ended') };
  if (t < start) {
    // Calendar days, not 24h windows: a contest starting tonight is "today", not "tomorrow".
    // Math.round absorbs the 23h/25h days around DST changes.
    const s = new Date(start);
    const startDay = new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const days = Math.round((startDay - today) / DAY_MS);
    const label =
      days <= 0
        ? i18n.t('contests:status.startsToday')
        : days === 1
          ? i18n.t('contests:status.startsTomorrow')
          : i18n.t('contests:status.startsInDays', { count: days });
    return { tone: 'accent', label };
  }
  const left = Math.ceil((end - t) / DAY_MS);
  return {
    tone: 'success',
    label: left <= 1 ? i18n.t('contests:status.activeEndsToday') : i18n.t('contests:status.activeDaysLeft', { count: left }),
  };
};

export const formatShortDate = (d: Date | string): string =>
  new Date(d).toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' });
